import { useAppStore } from "@/stores";
import useSWR from "swr";
import { getDeals } from "@/lib/supabase";
import { Loader } from "@/components/Loader";
// import { LoadingError } from "@/components/LoadingError";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  startOfQuarter,
  endOfYear,
  subQuarters,
  addMonths,
  isAfter,
  // subMonths,
  // startOfMonth,
  endOfMonth,
  // isBefore,
} from "date-fns";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
} from "lucide-react";
import { getDealOwner } from "@revelate/utils";
import { getAPI } from "@/lib/api";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import { getDealEmails } from "@/lib/supabase";
import {
  formatCurrencyValue,
} from "../lib/risk";
import { Switch } from "@/components/ui/switch";
import { AtRiskDealsDataTable } from "../components/AtRiskDealsDataTable";
import { AtRiskDealDetails } from "../components/AtRiskDealDetails";
import { RiskHighlightCard } from "../components/RiskHighlightCard";
import {
  ForecastChart,
  // ForecastWaterfallChart,
} from "../components/charts";
import { SectionCard } from "../components/SectionCard";
import { usePostHog } from "@posthog/react";
import { usePageTimeTracking } from "@/lib/posthog/usePageTimeTracking";

// Important dates
const now = new Date();
// const startOfThisQuarter = startOfQuarter(now);
// const startOfPrevQuarter = startOfQuarter(subQuarters(now, 1));
const startOf2QuartersAgo = startOfQuarter(subQuarters(now, 2));
const endOfThisYear = endOfYear(now);
// const startOfThisMonth = startOfMonth(now);
// const startOfLastMonth = startOfMonth(subMonths(now, 1));
const endOf6Months = endOfMonth(addMonths(now, 5));

const DEALS_TABLE_COUNT = 5;

type ForecastTimePeriod =
  | "prev_quarter"
  | "this_quarter"
  | "next_quarter"
  | "next_6_months"
  | "this_year"
  ;

import { useCanonicalForecast } from "../expected-close-v2/useCanonicalForecast";

export function RevenueIntelligenceForecast() {
  const [view, setView] = useState<"dashboard" | "deals" | "deal">("deals");
  const [viewingDealId, setViewingDealId] = useState<number | null>(null);
  const [forecastTimePeriod, setForecastTimePeriod] = useState<ForecastTimePeriod>("this_year");
  const [isForecastAccumulated, setIsForecastAccumuated] = useState<boolean>(false);
  const [globalSelectedPipeline, setGlobalSelectedPipeline] = useState<string>("all");
  const [globalSelectedOwner, setGlobalSelectedOwner] = useState<string>("all");
  const canonicalForecast = useCanonicalForecast(forecastTimePeriod, isForecastAccumulated, globalSelectedPipeline, globalSelectedOwner);

  const { currentUser, companyCurrency } = useAppStore((state) => state);

  const posthog = usePostHog();
  usePageTimeTracking("l2_forecast", posthog);

  const startOfForecastTimePeriod = startOf2QuartersAgo;

  const endOfForecastTimePeriod = isAfter(endOfThisYear, endOf6Months)
    ? endOfThisYear
    : endOf6Months;

  const {
    data: dealsForForecast,
    // error: dealsForForecastError,
    // isLoading: isLoadingDealsForForecast,
  } = useSWR(
    [
      'query:dealsForForecast',
      currentUser ? currentUser?.company_id.toString() : null,
      startOfForecastTimePeriod.toISOString(),
      endOfForecastTimePeriod.toISOString(),
    ],
    ([_, companyId, from, to]) => getDeals(companyId, {
      dateRange: { from, to },
      nonZeroValue: true,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: pipelines,
    // error: pipelinesError,
    // isLoading: isLoadingPipelines,
  } = useSWR(
    [
      'query:pipelines',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getAPI(`providers/hubspot/pipelines/${companyId}`),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const pipelinesForSelect = pipelines && pipelines.results
    ? [
      {
        value: "all",
        label: "All Pipelines",
      },
      ...pipelines.results
        .map((p: any) => ({
          value: p.id,
          label: p.label,
        })),
    ]
    : []

  let ownersForSelect = [{
    value: "all",
    label: "All Sales Reps",
  }];

  if (dealsForForecast) {
    const forecastDealOwners = dealsForForecast.map(d => getDealOwner(d));

    const uniqueOwnersMap = new Map();
    forecastDealOwners.forEach(owner => {
      if (owner?.id && !uniqueOwnersMap.has(owner.id)) {
        uniqueOwnersMap.set(owner.id, owner);
      }
    });
    const uniqueOwnersArray = Array.from(uniqueOwnersMap.values());

    ownersForSelect = [
      ...ownersForSelect,
      ...uniqueOwnersArray.map(owner => ({
        value: owner.id,
        label: `${owner.first_name} ${owner.last_name}`,
      }))
    ];
  }

  const {
    data: dealEmails,
    // error: dealEmailsError,
    // isLoading: isLoadingDealEmails,
  } = useSWR(
    [
      'query:dealEmails',
      viewingDealId,
    ],
    ([_, dealId]) => dealId === null ? null : getDealEmails(dealId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  if (canonicalForecast.error) return <div role="alert">Forecast data could not be loaded.</div>;
  if (canonicalForecast.isLoading || !canonicalForecast.position) return <Loader />;
  const chartDataForecast = canonicalForecast.chartData;
  const { forecast, target: revenueTarget, gapToTarget: revenueGap } = canonicalForecast.position;
  const dealsWithForecastRisk = canonicalForecast.getDealsForMonths(new Set(canonicalForecast.chartData
    .filter(row => !row.is_context).map(row => row.month)));

  return (
    <div>
      {view === "deals" && (
        <div className="space-y-4">
          <Card className="border-none bg-transparent">
            <CardContent className="px-0">
              <div className="flex flex-col gap-4">
                <div className="flex flex-row justify-between items-center">
                  <h1>Forecast</h1>
                  {/* Global filters */}
                  <div className="flex flex-row justify-end items-center gap-x-3">
                    <p className="text-sm text-gray-500">Filters</p>
                    <div className="flex flex-row gap-2">
                      <div>
                        <Select
                          value={forecastTimePeriod}
                          onValueChange={v => setForecastTimePeriod(v as ForecastTimePeriod)}
                        >
                          <SelectTrigger className="h-8 w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem key="prev_quarter" value="prev_quarter">
                              Last quarter
                            </SelectItem>
                            <SelectItem key="this_quarter" value="this_quarter">
                              This quarter
                            </SelectItem>
                            <SelectItem key="next_quarter" value="next_quarter">
                              Next quarter
                            </SelectItem>
                            <SelectItem key="next_6_months" value="next_6_months">
                              Next 6 months
                            </SelectItem>
                            <SelectItem key="this_year" value="this_year">
                              This year
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-row gap-2 items-center">
                        <Select
                          value={globalSelectedPipeline}
                          onValueChange={v => setGlobalSelectedPipeline(v)}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {pipelinesForSelect.map((p: any) => (
                              <SelectItem key={p.value} value={p.value}>
                                {p.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex flex-row gap-2 items-center">
                        <Select
                          value={globalSelectedOwner}
                          onValueChange={v => setGlobalSelectedOwner(v)}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ownersForSelect.map((owner: any) => (
                              <SelectItem key={owner.value} value={owner.value}>
                                {owner.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col lg:grid lg:grid-cols-3 gap-2 w-full pb-4">
                  <RiskHighlightCard
                    title="Forecast"
                    headline={`${formatCurrencyValue(forecast, companyCurrency, 1)}`}
                  //change={0}
                  //isChangeGood={true}
                  //changeDescription="vs 30d ago"
                  />
                  <RiskHighlightCard
                    title="Revenue target"
                    headline={`${formatCurrencyValue(revenueTarget, companyCurrency, 1)}`}
                  />
                  <RiskHighlightCard
                    title="Revenue gap"
                    headline={`${revenueGap > 0 ? "+" : ""}${formatCurrencyValue(revenueGap, companyCurrency, 1)}`}
                  //change={0}
                  //isChangeGood={true}
                  //changeDescription="vs 30d ago"
                  />
                </div>
                <SectionCard className="col-span-3">
                  <div className="flex flex-col gap-y-4">
                    <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                      <div className="w-full flex flex-row justify-between mb-10">
                        {/* Header */}
                        <div>
                          <p className="font-medium text-sm">
                            Forecast to target
                          </p>
                          <p className="text-xs text-gray-500">
                            Won revenue and expected revenue from open Deals
                          </p>
                        </div>
                        {/* Forecast toggles */}
                        <div className="flex flex-row gap-x-2 items-center">
                          <div className="flex flex-row gap-x-2 items-center">
                            <p className="text-sm text-gray-500">
                              Accumulated
                            </p>
                            <Switch
                              checked={isForecastAccumulated}
                              onCheckedChange={v => setIsForecastAccumuated(v)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="pb-4">
                        <ForecastChart
                          data={chartDataForecast}
                          companyCurrency={companyCurrency}
                        />
                      </div>
                    </div>
                  </div>
                </SectionCard>

                {/*
                <SectionCard className="col-span-3">
                  <div className="flex flex-col gap-y-4">
                    <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                      <div className="w-full flex flex-row justify-between mb-10">
                        <div>
                          <p className="font-medium text-sm">
                            Driver breakdown
                          </p>
                          <p className="text-xs text-gray-500">
                            What moved the forecast in a 30d period
                          </p>
                        </div>
                      </div>

                      <ForecastWaterfallChart
                        data={chartDataWaterfall}
                        companyCurrency={companyCurrency}
                      />
                    </div>
                  </div>
                </SectionCard>
                */}

                <AtRiskDealsDataTable
                  showForecastImpact
                  forecastValueDisplay="contribution"
                  companyCurrency={companyCurrency}
                  deals={dealsWithForecastRisk.slice(0, DEALS_TABLE_COUNT)}
                  onClickDeal={(dealId) => {
                    setViewingDealId(dealId)
                    setView("deal")
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {view === "deal" && viewingDealId !== null && (
        <div className="space-y-1">
          <Button
            onClick={() => {
              setView("deals");
              setViewingDealId(null);
            }}
            variant="ghost"
            className="bg-gray-100"
          >
            <div className="flex flex-row gap-2 items-center">
              <ArrowLeft size="14" />
              <span>
                Back
              </span>
            </div>
          </Button>
          <AtRiskDealDetails
            deal={dealsWithForecastRisk.find((d: any) => d.id === viewingDealId)}
            dealEmails={dealEmails}
            riskCategory="forecast"
          />
        </div>
      )}
    </div>
  );
}
